import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CssBaseline from '@mui/material/CssBaseline';
import FormControlLabel from '@mui/material/FormControlLabel';
import Divider from '@mui/material/Divider';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MuiCard from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import AppTheme from '../../components/shared-theme/AppTheme';
import InputAdornment from '@mui/material/InputAdornment';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import DynamicsLogo from '../../components/images/DynamicsLogo';
import TransitionMark from '../Transitions/TransitionMark';
import { registerWithEmail, registerWithWhatsApp } from '../../utils/api';
import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';

const Card = styled(MuiCard)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'center',
    width: '100%',
    padding: theme.spacing(4),
    gap: theme.spacing(2),
    margin: 'auto',
    [theme.breakpoints.up('sm')]: {
        maxWidth: '450px',
    },
    boxShadow:
        'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
}));

const RegisterContainer = styled(Stack)(({ theme }) => ({
    height: 'calc((1 - var(--template-frame-height, 0)) * 100dvh)',
    minHeight: '100%',
    padding: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(4),
    },
    '&::before': {
        content: '""',
        display: 'block',
        position: 'absolute',
        zIndex: -1,
        inset: 0,
        backgroundImage:
            'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
        backgroundRepeat: 'no-repeat',
    },
}));

export default function Register(props) {
    const navigate = useNavigate();
    const [emailError, setEmailError] = React.useState(false);
    const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
    const [passwordError, setPasswordError] = React.useState(false);
    const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
    const [showPassword, setShowPassword] = React.useState(false);
    const [isWhatsappRegister, setIsWhatsappRegister] = React.useState(false);
    const [whatsappValue, setWhatsappValue] = React.useState('');


    const handleWhatsappChange = (event) => {
        let value = event.target.value.replace(/[^0-9]/g, '');
        if (value.length > 0 && !value.startsWith('08')) {
            value = '08' + value.slice(2);
        }
        setWhatsappValue(value);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (validateInputs()) {
            const data = new FormData(event.currentTarget);
            try {
                if (isWhatsappRegister) {
                    // WhatsApp Registration
                    const whatsapp = data.get('whatsapp');
                    const response = await registerWithWhatsApp(whatsapp);
                    console.log('WhatsApp registration response:', response);

                    // Navigate to OTP page with register-whatsapp method
                    navigate('/otp', {
                        state: {
                            method: 'register-whatsapp', // More specific method
                            contact: whatsapp
                        }
                    });
                } else {
                    // Email Registration
                    const email = data.get('email');
                    const name = data.get('name');
                    const password = data.get('password');
                    const confirmPassword = data.get('confirm_password');
                    const response = await registerWithEmail(email, name, password, confirmPassword);
                    console.log('Email registration response:', response);

                    // Navigate to OTP page with register-email method
                    navigate('/otp', {
                        state: {
                            method: 'register-email', // More specific method
                            contact: email,
                            name: name,
                            email: email,
                            password: password
                        }
                    });
                }
            } catch (error) {
                console.error('Registration error:', error);
                setEmailError(true);
                setEmailErrorMessage('Gagal mendaftar. Mohon coba lagi.');
            }
        }
    };

    const validateInputs = () => {
        const email = document.getElementById('email');
        const password = document.getElementById('password');
        const confirmPassword = document.getElementById('confirm_password');
        const name = document.getElementById('name');
        const whatsapp = document.getElementById('whatsapp');

        let isValid = true;

        if (!isWhatsappRegister) {
            if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
                setEmailError(true);
                setEmailErrorMessage('Mohon masukkan email yang valid.');
                isValid = false;
            } else {
                setEmailError(false);
                setEmailErrorMessage('');
            }

            if (!name.value) {
                setEmailError(true);
                setEmailErrorMessage('Nama lengkap diperlukan.');
                isValid = false;
            } else {
                setEmailError(false);
                setEmailErrorMessage('');
            }

            if (!password.value || password.value.length < 6) {
                setPasswordError(true);
                setPasswordErrorMessage('Kata sandi minimal 6 karakter.');
                isValid = false;
            } else {
                setPasswordError(false);
                setPasswordErrorMessage('');
            }

            if (password.value !== confirmPassword.value) {
                setPasswordError(true);
                setPasswordErrorMessage('Konfirmasi kata sandi tidak cocok.');
                isValid = false;
            }
        } else {
            if (!whatsapp.value || !/^\d{10,15}$/.test(whatsapp.value)) {
                setEmailError(true);
                setEmailErrorMessage('Mohon periksa kembali nomor WhatsApp Anda.');
                isValid = false;
            } else {
                setEmailError(false);
                setEmailErrorMessage('');
            }
        }

        return isValid;
    };

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handleWhatsappRegister = () => {
        resetValidationStates();
        setIsWhatsappRegister(true);
    };

    const handleEmailRegister = () => {
        resetValidationStates();
        setIsWhatsappRegister(false);
    };

    const resetValidationStates = () => {
        setEmailError(false);
        setEmailErrorMessage('');
        setPasswordError(false);
        setPasswordErrorMessage('');
    };

    const [showTransition, setShowTransition] = useState(false);
    const location = useLocation();

    useEffect(() => {
        // Trigger transition on route change
        setShowTransition(true);
        const timer = setTimeout(() => setShowTransition(false), 2000); // Show for 2 seconds

        return () => clearTimeout(timer); // Clean up timeout on unmount
    }, [location]); // Dependency on location to trigger on route change

    return (
        <AppTheme {...props}>
            <CssBaseline enableColorScheme />
            {showTransition && <TransitionMark />} {/* Display transition animation */}
            {!showTransition && (
                <>
                    <RegisterContainer direction="column" justifyContent="space-between">
                        <Card variant="outlined">
                            <DynamicsLogo height="34px" />
                            <Typography
                                component="h1"
                                variant="h4"
                                sx={{ width: '100%', fontSize: 'clamp(1.5rem, 10vw, 1.15rem)' }}
                            >
                                Sign Up
                            </Typography>
                            <Box
                                component="form"
                                onSubmit={handleSubmit}
                                noValidate
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    width: '100%',
                                    gap: 2,
                                }}
                            >
                                {isWhatsappRegister ? (
                                    <FormControl>
                                        <FormLabel htmlFor="whatsapp">WhatsApp</FormLabel>
                                        <TextField
                                            id="whatsapp"
                                            type="tel"
                                            name="whatsapp"
                                            placeholder="Nomor WhatsApp"
                                            required
                                            fullWidth
                                            variant="outlined"
                                            size="small"
                                            inputMode="tel"
                                            value={whatsappValue}
                                            onChange={handleWhatsappChange}
                                            error={emailError}
                                            helperText={emailErrorMessage}
                                        />
                                    </FormControl>
                                ) : (
                                    <>
                                        <FormControl>
                                            <FormLabel htmlFor="name">Nama</FormLabel>
                                            <TextField
                                                error={emailError}
                                                helperText={emailErrorMessage}
                                                id="name"
                                                type="text"
                                                name="name"
                                                placeholder="Nama Lengkap"
                                                required
                                                fullWidth
                                                variant="outlined"
                                                size="small"
                                            />
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel htmlFor="email">Email</FormLabel>
                                            <TextField
                                                error={emailError}
                                                helperText={emailErrorMessage}
                                                id="email"
                                                type="email"
                                                name="email"
                                                placeholder="email@domain.com"
                                                required
                                                fullWidth
                                                variant="outlined"
                                                size="small"
                                            />
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel htmlFor="password">Kata Sandi</FormLabel>
                                            <TextField
                                                error={passwordError}
                                                helperText={passwordErrorMessage}
                                                id="password"
                                                name="password"
                                                placeholder="••••••"
                                                type={showPassword ? 'text' : 'password'}
                                                required
                                                fullWidth
                                                variant="outlined"
                                                size="small"
                                                slotProps={{
                                                    input: {
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <Button
                                                                    variant="text"
                                                                    onClick={handleClickShowPassword}
                                                                    size="small"
                                                                    sx={{
                                                                        backgroundColor: 'transparent',
                                                                        minWidth: 0,
                                                                        padding: 0,
                                                                        '&:hover': {
                                                                            backgroundColor: 'transparent',
                                                                        },
                                                                    }}
                                                                >
                                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                                </Button>
                                                            </InputAdornment>
                                                        ),
                                                    },
                                                }}
                                            />
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel htmlFor="confirm_password">Konfirmasi Kata Sandi</FormLabel>
                                            <TextField
                                                error={passwordError}
                                                helperText={passwordErrorMessage}
                                                id="confirm_password"
                                                name="confirm_password"
                                                placeholder="••••••"
                                                type={showPassword ? 'text' : 'password'}
                                                required
                                                fullWidth
                                                variant="outlined"
                                                size="small"
                                                slotProps={{
                                                    input: {
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <Button
                                                                    variant="text"
                                                                    onClick={handleClickShowPassword}
                                                                    size="small"
                                                                    sx={{
                                                                        backgroundColor: 'transparent',
                                                                        minWidth: 0,
                                                                        padding: 0,
                                                                        '&:hover': {
                                                                            backgroundColor: 'transparent',
                                                                        },
                                                                    }}
                                                                >
                                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                                </Button>
                                                            </InputAdornment>
                                                        ),
                                                    },
                                                }}
                                            />
                                        </FormControl>
                                    </>
                                )}
                                <FormControlLabel
                                    control={<Checkbox value="agree" color="primary" sx={{ transform: 'scale(0.8)' }} />}
                                    label="Saya setuju dengan syarat dan ketentuan"
                                    sx={{
                                        '& .MuiFormControlLabel-label': {
                                            fontSize: '0.7rem', // Smaller font size
                                        },
                                        margin: 0, // Optional: Reduce spacing around the label
                                    }}
                                />

                                <Button type="submit" fullWidth variant="contained">
                                    Daftar
                                </Button>
                            </Box>
                            <Divider>Atau</Divider>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={isWhatsappRegister ? handleEmailRegister : handleWhatsappRegister}
                            >
                                {isWhatsappRegister ? 'Daftar dengan Email' : 'Daftar dengan WhatsApp'}
                            </Button>
                            {/* Link to the Sign In page */}
                            <Typography sx={{ textAlign: 'center' }}>
                                Sudah punya akun?{' '}
                                <Link
                                    href="/signin"
                                    variant="body2"
                                    sx={{ alignSelf: 'center' }}
                                >
                                    Masuk
                                </Link>
                            </Typography>
                        </Card>
                    </RegisterContainer>
                </>
            )}
        </AppTheme>
    );
}
